import React from 'react';
import { Box, Flex, Grid, Text, Tooltip } from '@chakra-ui/react';
import { PairOfLabels, TennisGame, TennisPoint } from '../../utils/tennisScore';
import { BoardPlayer, pairText } from './tennisBoard';
import TennisServeBall from './TennisServeBall';

/** The game a click selects: an index into the log, or the game in play. */
export type GameSelection = number | 'current';

interface TennisGameStripProps {
  set: number;
  /** The set's result, or "in play". */
  result: string;
  players: [BoardPlayer, BoardPlayer];
  games: { game: TennisGame; index: number }[];
  /** The game in play or, for a stopped match, the game it stopped in. */
  current?: { points: TennisPoint[]; calls: PairOfLabels; serverId?: number; stopped: boolean };
  selected?: GameSelection;
  onSelect: (selection: GameSelection) => void;
}

const LANE_HEIGHT = '34px';
const GAME_WIDTH = '64px';

const nameOf = (players: [BoardPlayer, BoardPlayer], id?: number) =>
  players.find(p => p.competitor.id === id)?.competitor.name ?? 'unknown';

/** Break points the receiver had in a game: converted out of played, or how many the server saved. */
const breakPointsText = (game: TennisGame): { text: string; converted: boolean } | undefined => {
  const played = game.pointLog.filter(p => p.breakPoint);
  if (played.length === 0) {
    return undefined;
  }
  const converted = played.filter(p => p.winnerCompetitorId !== p.serverCompetitorId).length;
  return converted > 0
    ? { text: `${converted}/${played.length}`, converted: true }
    : { text: `${played.length} saved`, converted: false };
};

/**
 * The games of one set, left to right. Each player has a lane: the winner's lane is filled in their
 * colour and the ball marks who served, so a fill on the other lane from the ball is a break, framed in
 * red. Above each game the games in the set after it, below the score it was won from — a tiebreak's
 * final points — and the break points. A click selects the game.
 */
export const TennisGameStrip: React.FC<TennisGameStripProps> = ({
  set,
  result,
  players,
  games,
  current,
  selected,
  onSelect
}) => {
  const columns = games.length + (current ? 1 : 0);

  const lane = (player: BoardPlayer, game: TennisGame, index: number) => {
    const won = game.winnerCompetitorId === player.competitor.id;
    const isSelected = selected === index;
    return (
      <Tooltip
        label={`Game ${game.game}${game.tiebreak ? ' (tiebreak)' : ''}: ${nameOf(players, game.serverCompetitorId)} served, `
          + `${nameOf(players, game.winnerCompetitorId)} won${game.breakOfServe ? ', break of serve' : ''}`}
      >
        <Flex
          as="button"
          type="button"
          onClick={() => onSelect(index)}
          h={LANE_HEIGHT}
          position="relative"
          align="center"
          justify="center"
          borderRadius="md"
          borderWidth={won && game.breakOfServe ? '2px' : '1px'}
          borderColor={won ? (game.breakOfServe ? 'red.500' : `${player.scheme}.500`) : 'gray.200'}
          bg={won ? `${player.scheme}.100` : 'white'}
          outline={isSelected ? '2px solid' : undefined}
          outlineColor={isSelected ? 'gray.700' : undefined}
          outlineOffset="1px"
          _hover={{ borderColor: `${player.scheme}.400` }}
        >
          {game.serverCompetitorId === player.competitor.id && (
            <Box position="absolute" left="5px" top="50%" transform="translateY(-50%)" lineHeight={0}><TennisServeBall size={9} /></Box>
          )}
          {won && game.breakOfServe && (
            <Text fontSize="2xs" fontWeight="extrabold" letterSpacing="wide" color={`${player.scheme}.800`}>BRK</Text>
          )}
        </Flex>
      </Tooltip>
    );
  };

  const currentLane = (player: BoardPlayer) => current && (
    <Flex
      as="button"
      type="button"
      onClick={() => onSelect('current')}
      h={LANE_HEIGHT}
      position="relative"
      borderRadius="md"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="gray.400"
      bg="gray.50"
      outline={selected === 'current' ? '2px solid' : undefined}
      outlineColor={selected === 'current' ? 'gray.700' : undefined}
      outlineOffset="1px"
      title={current.stopped ? 'Game not finished' : 'Game in play'}
    >
      {!current.stopped && current.serverId === player.competitor.id && (
        <Box position="absolute" left="5px" top="50%" transform="translateY(-50%)" lineHeight={0}><TennisServeBall size={9} /></Box>
      )}
    </Flex>
  );

  const label = (text: string, color = 'gray.500') => (
    <Text fontSize="xs" fontWeight="semibold" color={color} alignSelf="center" pr={2} whiteSpace="nowrap">{text}</Text>
  );

  return (
    <Box mb={3}>
      <Flex align="baseline" gap={2} mb={1}>
        <Text fontSize="sm" fontWeight="bold">Set {set}</Text>
        <Text fontSize="sm" color="gray.500">{result}</Text>
      </Flex>
      <Box overflowX="auto" pb={1}>
        <Grid
          gridAutoFlow="column"
          templateRows={`20px ${LANE_HEIGHT} ${LANE_HEIGHT} 18px 16px`}
          templateColumns={`max-content repeat(${columns}, ${GAME_WIDTH})`}
          columnGap={1}
          rowGap={1}
          minW="max-content"
        >
          {label('Games')}
          {label(players[0].competitor.name, `${players[0].scheme}.600`)}
          {label(players[1].competitor.name, `${players[1].scheme}.600`)}
          {label('Score')}
          {label('Break pts')}

          {games.map(({ game, index }) => {
            const breakPoints = breakPointsText(game);
            return (
              <React.Fragment key={index}>
                <Text fontSize="xs" fontWeight="bold" textAlign="center" alignSelf="center">{pairText(game.gamesAfter)}</Text>
                {lane(players[0], game, index)}
                {lane(players[1], game, index)}
                <Text fontSize="xs" textAlign="center" alignSelf="center"
                      color={game.tiebreak ? 'orange.600' : 'gray.600'} fontWeight={game.tiebreak ? 'bold' : 'normal'}
                      title={game.tiebreak ? 'Tiebreak, final points' : 'Score before the winning point'}>
                  {game.tiebreak ? 'TB ' : ''}{pairText(game.wonFrom)}
                </Text>
                <Text fontSize="2xs" textAlign="center" alignSelf="center" whiteSpace="nowrap"
                      color={breakPoints?.converted ? 'red.600' : 'gray.500'} fontWeight="bold">
                  {breakPoints?.text ?? ''}
                </Text>
              </React.Fragment>
            );
          })}

          {current && (
            <>
              <Text fontSize="xs" textAlign="center" alignSelf="center" color="gray.500">{current.stopped ? 'stopped' : 'now'}</Text>
              {currentLane(players[0])}
              {currentLane(players[1])}
              <Text fontSize="xs" textAlign="center" alignSelf="center" color="gray.600">{pairText(current.calls)}</Text>
              <Box />
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default TennisGameStrip;
